import { reportMenuItems } from '@constants/Navigation/reportRoutes';
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  Stack,
  SvgIcon,
  SvgIconProps,
  Typography,
} from '@mui/material';
import { NavLinkBehavior } from 'components/NavLinkBehavior';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { slugify } from 'utils/slugify';
import { useRootACLContext } from '../../../components/RootACLProvider';
import { ReportMenuModel } from './ReportMenuItem';
import { Container } from './styled/Container';
import { ReportButton } from './styled/ReportButton';

export const ReportMenu: FC = (): JSX.Element => {
  return (
    <Container direction={'column'} spacing={'0.5rem'} sx={{ width: '100%' }}>
      <MenuTree menuItems={reportMenuItems} />
    </Container>
  );
};

const MenuTree: React.FC<{ menuItems: Array<ReportMenuModel> }> = ({ menuItems }) => {
  const { t } = useTranslation('common');
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const { isAllowed } = useRootACLContext();

  const toggleExpand = (path: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  const renderMenu = (items: Array<ReportMenuModel>) => {
    return items
      .filter(item => isAllowed(item.keyPermission!))
      .map(item => (
        <Box key={item.path} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {(item?.children || [])?.length > 0 ? (
            <Box>
              <Box
                key={item?.path}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  py: 1,
                  width: '100%',
                }}
                onClick={() => toggleExpand(item.path)}
              >
                <Typography
                  sx={{
                    color: theme => theme.palette.colors.gray.o90,
                    fontWeight: theme => theme.typography.fontWeightBold,
                  }}
                  variant="inter16"
                >
                  {t(`${item.name}` as any)}
                </Typography>
                <IconButton size="small" sx={{}}>
                  {expandedItems.has(item.path) ? (
                    <ChevronDownIcon sx={{ rotate: '180deg', color: '#939eaf' }} />
                  ) : (
                    <ChevronDownIcon sx={{ color: '#939eaf' }} />
                  )}
                </IconButton>
              </Box>
              <Collapse in={expandedItems.has(item.path)} timeout="auto">
                <Box>{item.children && renderMenu(item.children)}</Box>
              </Collapse>
            </Box>
          ) : (
            <ReportButton
              id={`${slugify(item.name)}-settings-link-menu`}
              LinkComponent={NavLinkBehavior}
              sx={{
                justifyContent: 'flex-start',
                fontWeight: 400,
                my: 0.5,
              }}
              href={item.path}
              fullWidth
              startIcon={item.icon}
              variant="outlined"
            >
              {t(`${item.name}` as any)}
            </ReportButton>
          )}
        </Box>
      ));
  };

  return (
    <Stack
      direction="column"
      spacing={1}
      divider={
        <Divider
          orientation="horizontal"
          sx={{
            borderColor: '#d2d7de',
            opacity: 1,
            my: 1,
            visibility: 'inherit',
            boxShadow: 'inset 0 -1px 0 0 #d2d7de',
          }}
        />
      }
    >
      {renderMenu(menuItems)}
    </Stack>
  );
};

const ChevronDownIcon = (props: SvgIconProps) => {
  return (
    <SvgIcon {...props}>
      <defs>
        <clipPath id="jnie2vbwfa">
          <path d="M1440 0v1080H0V0h1440z" />
        </clipPath>
      </defs>
      <g clipPath="url(#jnie2vbwfa)" transform="translate(-280 -24)">
        <path
          d="m286 33 6 6 6-6"
          stroke="currentColor"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </SvgIcon>
  );
};
